import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, ShieldCheck } from "lucide-react";
import { getApiUrl as resolveApiUrl } from "@/lib/runtimeConfig";
import { formatApiErrorDetail } from "@/lib/api";
import { AUTH_BUTTON_CLS, AUTH_INPUT_CLS, AuthError, AuthShell, BackToLogin, PASSWORD_HINT } from "@/components/auth/AuthShell";
import { InitResetSection } from "@/components/auth/InitResetSection";

const API = resolveApiUrl();

export default function InitSetupPage() {
    const navigate = useNavigate();
    const [status, setStatus] = useState(null); // {available, reason}
    const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        const probe = () => axios.get(`${API}/init/status`)
            .then((r) => { if (alive) setStatus(r.data); })
            .catch(() => { if (alive) setStatus((s) => s || { available: false, reason: "unreachable" }); });
        probe();
        const id = setInterval(probe, 15000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (form.password !== form.confirm) { setError("Passwords don't match"); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/init`, { name: form.name.trim() || "Admin", email: form.email.trim(), password: form.password });
            toast.success("Super admin created — sign in to continue");
            navigate("/login", { replace: true });
        } catch (e2) {
            if (e2?.response?.status === 410) setStatus({ available: false, reason: "already_used" });
            else setError(formatApiErrorDetail(e2?.response?.data?.detail) || "Setup failed.");
            setBusy(false);
        }
    };

    if (!status) {
        return (
            <AuthShell kicker="Initial setup" testId="init-page">
                <div className="text-sm text-white/50 py-12 text-center" data-testid="init-loading">Checking setup state…</div>
            </AuthShell>
        );
    }

    if (!status.available) {
        return (
            <AuthShell kicker="Initial setup" title="Setup already completed" testId="init-page">
                <div className="bg-white/[0.03] border border-white/10 rounded-md p-4 flex gap-3" data-testid="init-closed">
                    <Lock className="w-5 h-5 text-white/50 shrink-0 mt-0.5" strokeWidth={1.6} />
                    <div className="text-sm text-white/70">
                        {status.reason === "unreachable"
                            ? "The server could not be reached. Try again in a moment."
                            : "A super admin already exists, so this one-time setup page is closed. Sign in with an existing account or ask a super admin for an invite."}
                    </div>
                </div>
                <div className="mt-5"><BackToLogin /></div>
                {status.reason !== "unreachable" && <InitResetSection />}
            </AuthShell>
        );
    }

    return (
        <AuthShell kicker="Initial setup" title="Create the first super admin" testId="init-page">
            <div className="bg-[#7C3AED]/10 border border-[#7C3AED]/30 rounded-md p-3 mb-5 flex gap-2 text-xs text-white/70" data-testid="init-open">
                <ShieldCheck className="w-4 h-4 text-[#a17bff] shrink-0" />
                <span>This page works exactly once. After the account is created it closes permanently.</span>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="init-form">
                <label className="block">
                    <div className="text-xs text-white/50 mb-1.5">Name</div>
                    <input type="text" value={form.name} onChange={set("name")} maxLength={120} className={AUTH_INPUT_CLS} placeholder="Owner" data-testid="init-name-input" />
                </label>
                <label className="block">
                    <div className="text-xs text-white/50 mb-1.5">Email</div>
                    <input type="email" required value={form.email} onChange={set("email")} className={AUTH_INPUT_CLS} placeholder="you@example.com" spellCheck={false} data-testid="init-email-input" />
                </label>
                <label className="block">
                    <div className="text-xs text-white/50 mb-1.5">Password</div>
                    <div className="relative">
                        <input
                            type={show ? "text" : "password"} required minLength={12} maxLength={200} value={form.password} onChange={set("password")}
                            autoComplete="new-password" className={`${AUTH_INPUT_CLS} pr-10`} placeholder="≥ 12 characters" data-testid="init-password-input"
                        />
                        <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-2 text-white/40 hover:text-white/70 p-1"
                            aria-label={show ? "hide password" : "show password"} data-testid="init-password-toggle">
                            {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                    </div>
                    <div className="text-[11px] text-white/40 mt-1">{PASSWORD_HINT}</div>
                </label>
                <label className="block">
                    <div className="text-xs text-white/50 mb-1.5">Confirm password</div>
                    <input type={show ? "text" : "password"} required minLength={12} maxLength={200} value={form.confirm} onChange={set("confirm")} autoComplete="new-password" className={AUTH_INPUT_CLS} data-testid="init-confirm-input" />
                </label>
                <AuthError message={error} testId="init-error" />
                <button type="submit" disabled={busy} className={AUTH_BUTTON_CLS} data-testid="init-submit">
                    {busy ? "Creating…" : "Create super admin"}
                </button>
            </form>
        </AuthShell>
    );
}
